import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Snowflake, MessageCircle, Tag as TagIcon, Play, Clock, TrendingUp } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { getCategoriesForPosition, getCategoryScore, getOverallScore } from '../data/criteria';
import VideoPlayer from '../components/VideoPlayer';
import { InvitesAPI } from '../api';

function fmt(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function InviteView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    InvitesAPI.data(token).then(setData).catch(() => setErr('This link is no longer valid.'));
  }, [token]);

  const player = data?.player;
  const categories = useMemo(() => getCategoriesForPosition(player?.position), [player?.position]);
  const sortedEvals = useMemo(
    () => (data?.evaluations || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date)),
    [data]
  );
  const latest = sortedEvals[0];
  const overall = latest ? getOverallScore(latest.ratings, categories) : 0;
  const tags = data?.tags || [];
  const videos = data?.videos || [];

  if (err) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-600">{err}</div>;
  if (!data) return <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">Loading</div>;

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
            <Snowflake className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-sm">ShiftNotes<sup className="text-[9px] text-sky-600 ml-0.5">&trade;</sup></div>
            <div className="text-[10px] uppercase tracking-widest text-sky-600/80">Family View</div>
          </div>
          <Badge className="ml-auto bg-slate-100 text-slate-600 border-slate-200 text-[10px]">Read only</Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <img src={player.avatar} alt="" className="h-16 w-16 rounded-2xl ring-2 ring-slate-200" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl font-bold">{player.firstName} {player.lastName}</div>
              <div className="text-sm text-slate-500">#{player.jerseyNumber}  {player.team}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge className={player.position === 'Goalie' ? 'bg-violet-500/10 text-violet-700 border-violet-500/30' : 'bg-sky-500/10 text-sky-700 border-sky-500/30'}>{player.position}</Badge>
              </div>
            </div>
            {overall > 0 && (
              <div className="text-right">
                <div className="font-mono font-bold text-3xl bg-gradient-to-br from-sky-600 to-cyan-600 bg-clip-text text-transparent">{overall}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall</div>
              </div>
            )}
          </div>
        </div>

        {latest && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="h-4 w-4 text-sky-600" />
              <h2 className="font-display font-semibold">Latest Coach Feedback</h2>
            </div>
            <div className="text-xs text-slate-500">{latest.gameContext}  {new Date(latest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
            {latest.notes && <p className="mt-3 text-sm text-slate-700 italic leading-relaxed">{latest.notes}</p>}
            <div className="mt-4 space-y-2">
              {categories.map(c => {
                const v = parseFloat(getCategoryScore(latest.ratings, c.id, categories));
                return (
                  <div key={c.id}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-600">{c.name}</span><span className="font-mono text-sky-700">{v.toFixed(1)}</span></div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full" style={{ width: `${(v / 10) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TagIcon className="h-4 w-4 text-amber-600" />
            <h2 className="font-display font-semibold">Tagged Moments</h2>
            <span className="text-xs text-slate-500">({tags.length})</span>
          </div>
          {tags.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No tagged moments yet.</div>}
          <ul className="space-y-2">
            {tags.slice(0, 20).map(t => {
              const v = videos.find(x => x.id === t.videoId);
              const cat = categories.find(c => c.id === t.categoryId);
              return (
                <li key={t.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => v && setActive(v)} disabled={!v}
                      className="flex items-center gap-1.5 font-mono text-xs text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded px-2 py-1 hover:bg-sky-500/20 disabled:opacity-50">
                      <Play className="h-3 w-3" fill="currentColor" /> {fmt(t.timestampSec)}
                    </button>
                    <div className="text-xs text-slate-500 truncate">{v?.title || 'Clip'}</div>
                  </div>
                  <div className="mt-2 text-sm text-slate-900">{t.label}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span>{t.evaluatorName}</span>
                    {cat && <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">{cat.name}</Badge>}
                    {t.rating && <Badge className="bg-sky-500/10 text-sky-700 border-sky-500/30 font-mono text-[10px]">{t.rating}/10</Badge>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {active && (
          <div className="space-y-2">
            <VideoPlayer video={active} playerId={player.id} playerPosition={player.position} canTag={false} readOnly />
            <div className="text-right">
              <Button variant="ghost" size="sm" onClick={() => setActive(null)} className="text-slate-500 hover:text-slate-900">Close player</Button>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-sky-600" />
            <h2 className="font-display font-semibold">Clips</h2>
          </div>
          {videos.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No clips yet.</div>}
          <div className="grid grid-cols-2 gap-3">
            {videos.map(v => (
              <button key={v.id} onClick={() => setActive(v)}
                className="text-left bg-slate-50 border border-slate-200 rounded-lg overflow-hidden hover:border-sky-500/40 transition-colors">
                <div className="relative aspect-video bg-slate-100">
                  {v.source === 'youtube' ? (
                    <img src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400"><Play className="h-6 w-6" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-10 w-10 rounded-full bg-sky-500 flex items-center justify-center"><Play className="h-4 w-4 text-white ml-0.5" fill="currentColor" /></div>
                  </div>
                  {v.duration && (
                    <div className="absolute bottom-1.5 right-1.5 bg-black/70 px-1.5 py-0.5 rounded text-[10px] text-white flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> {v.duration}</div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-xs font-semibold line-clamp-2">{v.title}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{new Date(v.date).toLocaleDateString()}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="text-center text-[11px] text-slate-400 py-4">
          ShiftNotes<sup className="text-[8px] text-sky-600">&trade;</sup>  a product of NextShift Development LLC
        </div>
      </main>
    </div>
  );
}
